import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";

class ClientResponseError extends Error {
    public errors: ErrorMessage[] = [];

    public constructor(errors: ErrorMessage[]) {
        super("Error Encountered");
        this.errors = errors;
    }
}
/**
 * A response received from the MYLE API.
 * @template TData The data model type for the response body, or `void` if the response has no body.
 */
class ClientResponse<TData = void> {
    /**
     * The data model received from the API.
     *
     * Note: due to TypeScript limitations, this is a plain object cast to the model type; to use methods or getters on the model type, you'll need to create a new instance from the model.
     */
    public readonly data: TData;

    public readonly status: string;
    /**
     * The errors which occurred while contacting the API (e.g. timeout, server error, or errors provided by the API); or an empty array if no errors occurred.
     */
    public readonly errors: ErrorMessage[];

    /**
     * Whether the request was submitted successfully and the API returned a success code.
     */
    public get isSuccess(): boolean {
        return !this.errors.length
            && (this.data as any)?.Success !== false
            && (this.data as any)?.IsSuccess !== false;
    }

    /**
     * Construct an instance.
     * @param data The data model received from the API.
     * @param errors The errors which occurred while contacting the API (e.g. timeout, server error, or errors provided by the API); otherwise null or an empty array if no errors occurred.
     */
    public constructor(data: TData, errors: ErrorMessage[] | string | any, status: string = "") {
        this.data = data;
        this.status = status;

        if (!errors) {
            this.errors = [];
        }
        else if (errors.status === 403) {
            this.errors = [new ErrorMessage({
                Code: "NotAuthorizedActionLabel",
                Message: cacheStore.GetString("NotAuthorizedActionLabel")
            })];
        }
        else if (Array.isArray(errors)) {
            this.errors = errors.map(error => this.toErrorMessage(error));
        }
        else {
            this.errors = [this.toErrorMessage(errors)];
        }
    }

    /**
     * Throw ClientResponseError is response has errors
     */
    public throwIfError(): void {
        if (this.isSuccess)
            return;

        throw new ClientResponseError(this.errors);
    }

    /**
     * Parse an arbitrary error into an {@link ErrorMessage} representation.
     * @param error The error to parse.
     */
    private toErrorMessage(error: any): ErrorMessage {
        if (!error) {
            return new ErrorMessage({ Message: cacheStore.Get("UnexpectedErrorMessage") });
        }
        if (typeof error === "string") {
            return new ErrorMessage({ Message: error });
        }
        if (error.Message) {
            return new ErrorMessage(error);
        }
        if (error instanceof Error) {
            return new ErrorMessage({ Message: `${error.message}\n${error.stack}`.trim() });
        }
        return new ErrorMessage({ Message: JSON.stringify(error) });
    }
}

export { ClientResponse, ClientResponseError };
